import { Row } from '@/components/SettingsLayout';
import styles from '../styles.module.css';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/constants';
import { CHART_COLORS } from '@/lib/chart-utils';
import { OrderMetricBy, TopOrganization } from '@/services/Dashboard/dashboard.service';
import { formatCurrency } from '@/utils/currency';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyTypes } from '@/types';
import { MAX_TOP_ITEMS } from './TopOrganizations';
import { CurrencyCode } from '@final-commerce/common';

type Props = {
  data: TopOrganization[] | undefined;
  onSortChange: (value: string) => void;
  entityType: CompanyTypes;
};
const Legend = ({ data, onSortChange, entityType }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isPlaceholder = (company: TopOrganization) => company.companyId?.startsWith('placeholder-');

  const handleNameClick = (company: TopOrganization) => {
    if (isPlaceholder(company)) return;
    const path =
      entityType === CompanyTypes.ORGANIZATION
        ? `${ROUTES.CDK}/${ROUTES.ORGANIZATIONS}/${company.companyId}/profile`
        : `${ROUTES.CDK}/${ROUTES.COMPANIES}/${company.companyId}/dashboard`;
    navigate(path);
  };

  const generatePlaceholderLegendData = (count: number): TopOrganization[] => {
    return Array.from({ length: count }, (_, index) => ({
      _id: `placeholder-${index}`,
      companyId: `placeholder-company-${index}`,
      companyName: '-',
      totalTransaction: 0,
      totalPaymentVolume: 0,
    }));
  };

  const displayData = useMemo<TopOrganization[]>(() => {
    const base = data && data.length > 0 ? data.slice(0, MAX_TOP_ITEMS) : [];
    if (base.length >= MAX_TOP_ITEMS) return base;
    const placeholdersNeeded = MAX_TOP_ITEMS - base.length;
    return [...base, ...generatePlaceholderLegendData(placeholdersNeeded)];
  }, [data]);

  if (!data) {
    return (
      <div className={styles.legendContainer}>
        <span className='text-lg font-medium leading-[132%]'>{t('Shared.not_found')}</span>
      </div>
    );
  }
  return (
    <div className={styles.legendContainer}>
      <ul className={`${styles.legendList} ${styles.legendNameList}`}>
        <li>
          <span className='text-xs  text-muted-foreground'>{t('Dashboard.company_name')}</span>
        </li>
        {displayData.map((company, index) => {
          const clickable = !isPlaceholder(company);
          return (
            <li
              key={index}
              data-testid={`company-name-legend-${index}`}
              onClick={() => handleNameClick(company)}
              style={clickable ? { cursor: 'pointer' } : undefined}
            >
              <span className='text-sm  text-primary'>{company.companyName}</span>
            </li>
          );
        })}
      </ul>
      <Row gapSize='12' fullWidth={false} align='start'>
        <ul className={`${styles.legendList} ${styles.alignCenter}`}>
          <li
            onClick={() => onSortChange(OrderMetricBy.TOTAL_PAYMENT_VOLUME)}
            className={styles.sortItem}
          >
            <span className='text-xs font-normal text-muted-foreground'>
              {t('Dashboard.total_payment_volume')}
            </span>
          </li>
          {displayData.map((company, index) => {
            return (
              <li key={index} data-testid={`total-payment-volume-legend-${index}`}>
                <Row>
                  <span
                    className={`${styles.legendItem} ${styles.alignCenter}`}
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  ></span>
                  <span className='text-sm font-light leading-[140%]'>
                    {company.currency
                      ? formatCurrency(
                          Number(company.totalPaymentVolume),
                          company.currency as CurrencyCode,
                        )
                      : String(company.totalPaymentVolume ?? 0)}
                  </span>
                </Row>
              </li>
            );
          })}
        </ul>
        <ul className={`${styles.legendList} ${styles.alignCenter}`}>
          <li
            onClick={() => onSortChange(OrderMetricBy.TOTAL_TRANSACTION)}
            className={styles.sortItem}
          >
            <span className='text-xs font-normal text-muted-foreground'>
              {t('Dashboard.transaction_count')}
            </span>
          </li>
          {displayData.map((company, index) => {
            return (
              <li key={index} data-testid={`transaction-count-legend-${index}`}>
                <Row>
                  <span
                    className={`${styles.legendItem}`}
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  ></span>
                  <span className='text-sm font-light leading-[140%]'>
                    {company.totalTransaction}
                  </span>
                </Row>
              </li>
            );
          })}
        </ul>
      </Row>
    </div>
  );
};

export default Legend;
